import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { _get } from "../../services/apiClient";
import { useCart } from "../../CartContext";
import { useCartActions } from "../../hooks/useCartActions";
import { formatCurrency } from "../../helper/currency";
import Slider from "react-slick";
import { Galleria } from "primereact/galleria";
import { FaShareAlt, FaWhatsapp } from "react-icons/fa";
import ZoomImage from "../../helper/zoomin/zoomimage";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "./productDetail.css";

function ProductDetail({ cart, setCart }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [images, setImages] = useState([]);
  const [showFullDetails, setShowFullDetails] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const { getQuantityForVariant } = useCart();
  const CHARACTER_LIMIT = 150;

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await _get(`/products/${id}`);
        const productData = response.data;
        setProduct(productData);

        if (productData.images?.length > 0) {
          const formatted = productData.images.map((img) => ({
            itemImageSrc: `${process.env.REACT_APP_IMAGE_BASE_URL}${img}`,
            thumbnailImageSrc: `${process.env.REACT_APP_IMAGE_BASE_URL}${img}`,
            alt: productData.productName,
          }));
          setImages(formatted);
        }
      } catch (err) {
        console.error("Error fetching product:", err);
      }
    };
    fetchProduct();
  }, [id]);

  // Use the custom hook for all cart operations
  const {
    selectedVariant,
    quantity,
    handleAddToCart,
    handleIncreaseQuantity,
    handleDecreaseQuantity,
    handleVariantSelect,
    setSelectedVariant,

  } = useCartActions(
    product,
    product?.variants?.[0] || null,
    cart,
    setCart
  );

  // Stock checks
  const hasAnyStock = product?.variants?.some(v => v.availableStock > 0);
  const selectedVariantStock = selectedVariant?.availableStock || 0;

  useEffect(() => {
    if (!product || !product.variants?.length) return;
    if (selectedVariant) return;

    // Auto-select first in-stock variant
    const inStockVariant = product.variants.find(v => v.availableStock > 0);
    if (inStockVariant) {
      setSelectedVariant(inStockVariant);
    } else {
      setSelectedVariant(product.variants[0]);
    }
  }, [product, setSelectedVariant]);



  const handleGoBack = () => navigate(-1);

  const handleShare = async () => {
    const shareData = {
      title: product.productName,
      text: `Check out this product: ${product.productName}`,
      url: window.location.href,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log("Share canceled or failed", err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(window.location.href);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2000);
      } catch (error) {
        console.error("Copy failed:", error);
      }
    }
  };

  const handleWhatsAppShare = () => {
    const message = `Check out this product: ${product.productName} - ${window.location.href}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const carouselSettings = {
    dots: true,
    infinite: false,
    speed: 300,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
  };

  const itemTemplate = (item) => (<ZoomImage src={item.itemImageSrc} alt={item.alt} />);
  const thumbnailTemplate = (item) => (
    <img
      src={item.thumbnailImageSrc}
      alt={item.alt}
      style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "4px" }}
    />
  );

  const renderProductDetails = (details) => {
    if (!details) return "No details available";
    if (details.length <= CHARACTER_LIMIT) return details;
    if (showFullDetails) {
      return (
        <>
          {details}{" "}
          <span
            className="read-more-link"
            onClick={() => setShowFullDetails(false)}
            style={{ color: "blue", cursor: "pointer" }}
          >
            Read Less
          </span>
        </>
      );
    }
    return (
      <>
        {details.substring(0, CHARACTER_LIMIT)}...{" "}
        <span
          className="read-more-link"
          onClick={() => setShowFullDetails(true)}
          style={{ color: "blue", cursor: "pointer" }}
        >
          Read More
        </span>
      </>
    );
  };

  if (!product || !selectedVariant) {
    return <div>Loading...</div>;
  }

  // Price block
  const PriceDisplay = ({ variant }) => {
    if (!variant) return null;
    if (variant.offerPrice) {
      const originalPrice = variant.price;
      const offerPrice = variant.offerPrice;
      const discount = Math.round(((originalPrice - offerPrice) / originalPrice) * 100);
      return (
        <>
          <span className="original-price">{formatCurrency(originalPrice)}</span>
          <span className="offer-price">{formatCurrency(offerPrice)}</span>
          <span className="discount-badge">{discount}% OFF</span>
        </>
      );
    }
    return <span className="offer-price">{formatCurrency(variant.price)}</span>;
  };

  // Low Stock Warning Component
  const LowStockWarning = ({ stock }) => {
    if (stock === 0 || stock > 5) return null;

    return (
      <div
        style={{
          color: 'red',
          padding: '8px 12px',
          fontSize: '13px',
          fontWeight: '500',
          marginTop: '10px'
        }}
      >
        😔 Only {stock} left!
      </div>
    );
  };

  // Variant selector with quantity indicators
  const VariantSelector = () => (
    <div className="variants-section mt-3">
      <h6 className="section-title">Available Offers</h6>
      <div className="d-flex flex-wrap" style={{ gap: '10px' }}>
        {product.variants.map((variant, index) => {
          const variantQty = getQuantityForVariant(product._id, variant._id);
          return (
            <button
              key={variant._id || index}
              className={`btn btn-sm ${selectedVariant._id === variant._id ? 'btn-danger' : 'btn-outline-secondary'}`}
              onClick={() => handleVariantSelect(variant)}
              style={{ position: 'relative' }}
            >
              {variant.quantity} {variant.unit}
              {variantQty > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    fontSize: '11px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold'
                  }}
                >
                  {variantQty}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  // Product overview section
  const ProductOverview = () => (
    <div className="product-overview-section mt-4">
      <h4 className="section-title">Product Overview</h4>
      <div className="overview-block">
        <h6>Product Details</h6>
        <p>{renderProductDetails(product.productDetails)}</p>
      </div>
      <div className="overview-block">
        <h6>Key Features</h6>
        <div
          className="key-features"
          dangerouslySetInnerHTML={{
            __html: product.keyFeatures || "<p>No key features listed.</p>",
          }}
        ></div>
      </div>
      {product.productSpecifications?.length > 0 && (
        <div className="overview-block">
          <h6>Product Specifications</h6>
          <table className="spec-table">
            <tbody>
              {product.productSpecifications.map((spec, idx) => (
                <tr key={idx}>
                  <td className="spec-key">{spec.key}</td>
                  <td className="spec-value">{spec.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Cart controls for selected variant
  const CartControls = () => {
    const isInCart = quantity > 0;

    if (!hasAnyStock || selectedVariantStock === 0) {
      return (
        <div className="mt-3">
          <button
            className="btn custom-rounded-btn px-3 btn-sm text-white fw-bold"
            style={{ backgroundColor: "gray", cursor: "not-allowed" }}
            disabled
          >
            OUT OF STOCK
          </button>
        </div>
      );
    }


    if (isInCart) {
      return (
        <div className="mt-3">
          <div className="btn-group">
            <button
              className="btn-sm btn btn-outline-secondary"
              onClick={handleDecreaseQuantity}
            >
              <i className="feather-minus"></i>
            </button>
            <span
              className="btn-sm btn btn-outline-secondary"
              style={{ cursor: "default" }}
            >
              {quantity}
            </span>
            <button
              className="btn-sm btn btn-outline-secondary"
              onClick={handleIncreaseQuantity}
            >
              <i className="feather-plus"></i>
            </button>
          </div>
          <LowStockWarning stock={selectedVariantStock} />
        </div>
      );
    }

    return (
      <div className="mt-3">
        <button
          className="btn custom-rounded-btn px-3 btn-sm text-white fw-bold"
          style={{ backgroundColor: "Salmon" }}
          onClick={handleAddToCart}
          disabled={selectedVariantStock === 0}
        >
          ADD TO CART
        </button>
        <LowStockWarning stock={selectedVariantStock} />
      </div>
    );
  };


  return (
    <div className="product-detail-container">
      <div className="back-button">
        <button className="btn btn-outline-secondary" onClick={handleGoBack}>
          &#8592; Back
        </button>
      </div>

      {/* Desktop Layout */}
      <div className="desktop-only">
        <div className="left-panel">
          <Galleria
            value={images}
            numVisible={4}
            style={{ maxWidth: "600px" }}
            circular
            showThumbnails
            showItemNavigators
            item={itemTemplate}
            thumbnail={thumbnailTemplate}
            thumbnailsPosition="left"
          />
        </div>

        <div className="right-panel">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h3>{product.productName}</h3>
            <button className="share-button" onClick={handleShare}>
              <FaShareAlt className="share-icon" /> Share
            </button>
          </div>

          {product.variants.length > 1 && <VariantSelector />}

          <p className="mt-3">
            <strong>Price: </strong>
            <PriceDisplay variant={selectedVariant} />
          </p>

          <p>
            <strong>Manufacture Date: </strong>
            {selectedVariant.manufactureDate
              ? new Date(selectedVariant.manufactureDate).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
              : "N/A"}

          </p>
          <p>
            <strong>Expiry Date: </strong>
            {selectedVariant.expiryDate
              ? new Date(selectedVariant.expiryDate).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
              : "N/A"}

          </p>

          <CartControls />
          <ProductOverview />
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="mobile-only">
        <div style={{ position: "relative" }}>
          <Slider {...carouselSettings} className="mobile-carousel">
            {product.images?.map((img, idx) => (
              <div key={idx}>
                <img
                  src={`${process.env.REACT_APP_IMAGE_BASE_URL}${img}`}
                  alt={`slide-${idx}`}
                />
              </div>
            ))}
          </Slider>
          <button className="mobile-share-button" onClick={handleShare}>
            <FaShareAlt />
          </button>
          <button className="mobile-whatsapp-button" onClick={handleWhatsAppShare}>
            <FaWhatsapp style={{ color: "#25D366", fontSize: "20px" }} />
          </button>
        </div>

        <div className="mt-3">
          <h3>{product.productName}</h3>
          {product.variants.length > 1 && <VariantSelector />}
          <p className="mt-3">
            <strong>Price: </strong>
            <PriceDisplay variant={selectedVariant} />
          </p>
          <p>
            <strong>Manufacture Date: </strong>
            {selectedVariant.manufactureDate
              ? new Date(selectedVariant.manufactureDate).toDateString()
              : "N/A"}
          </p>
          <p>
            <strong>Expiry Date: </strong>
            {selectedVariant.expiryDate
              ? new Date(selectedVariant.expiryDate).toDateString()
              : "N/A"}
          </p>
          <CartControls />
          <ProductOverview />
        </div>
      </div>

      <div className={`copy-toast ${showToast ? "show" : ""}`}>Link copied!</div>
    </div>
  );
}

export default ProductDetail;